import { ChevronDownIcon } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode, RefObject } from 'react';
import { Command, CommandItem, CommandList } from '@voll-intelligence/ui-kit/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@voll-intelligence/ui-kit/ui/popover';
import {
  FIELD_CONTROL_SIZE,
  FieldShell,
  fieldMessageId,
  fieldToneBorder,
  type FieldShellExtras,
} from './field_shell.js';

/**
 * O CAMPO DE SELEÇÃO POR BUSCA REMOTA dos formulários dos cards — o irmão
 * assíncrono do `SelectField`: mesma anatomia (rótulo em cima, controle
 * embaixo, largura da coluna), catálogo que NÃO cabe numa lista fechada.
 *
 * ## Por que ele existe (e não o `PlaceField` do DS)
 *
 * A mecânica aqui é promovida do `PlaceField` (`intelligence-ui-kit`): o
 * popover do DS sobre `Command`, o input que encaminha as teclas da lista pro
 * root do cmdk, o debounce com id monotônico de request. O que NÃO vem é o
 * vocabulário de praça — `PlaceSuggestion` fecha o glifo num conjunto
 * geográfico (`airport`/`city`/`lodging`), `PlaceFieldValue.code` é o IATA, e a
 * pílula redonda é o gatilho do planner de voo. Centro de custo, projeto e
 * afins não são lugares: reusar aquele componente aqui seria vestir contabilidade
 * de aeroporto.
 *
 * Este é o componente genérico que faltava — em Tailwind sobre os tokens do DS
 * (destino da migração), e não nos aliases `--v2-*` das folhas legadas. O
 * `.genui2` no embrulho é o de sempre: os cards não vivem sob a raiz de tema do
 * DS, e sem a classe os tokens não resolvem.
 *
 * Migração: os hosts de praça (route_field do aéreo, `Onde` do hotel) seguem no
 * `PlaceField` — eles têm painel próprio, seções de sugestão e abertura
 * controlada pro auto-avanço. O que este campo promove é a BASE comum; unificar
 * os dois é fatia posterior.
 */

export interface AsyncSelectOption {
  value: string;
  label: string;
  /** 2ª linha fraca da opção (opcional) — o `name` cru, uma unidade, um CNPJ. */
  description?: string;
}

export interface AsyncSelectFieldProps extends FieldShellExtras {
  /** Nome visível E acessível do campo. */
  label: string;
  /** O valor escolhido (a chave do catálogo, não o rótulo). */
  value: string;
  /**
   * Rótulo do valor já escolhido, pra o campo ocioso mostrar palavra em vez de
   * id. O dono da escolha é quem guarda: `onChange` devolve `(value, label)` e
   * o rótulo volta por aqui — cache local viraria uma segunda cópia que
   * envelhece quando o host reescreve o valor.
   *
   * `||` e não `??` na resolução: rótulo vazio é AUSÊNCIA de rótulo, e o
   * nullish deixaria o campo em branco com um valor selecionado por baixo.
   */
  selectedLabel?: string;
  placeholder?: string;
  /** Texto abaixo de `minChars` ("Digite para buscar"). */
  promptText: string;
  loadingText: string;
  /** Busca sem resultado — e também a busca que FALHOU: o fio não traz um
   *  segundo fato a dizer, e "não achei" é honesto nos dois casos. */
  emptyText: string;
  /** Busca remota. PRECISA ser referencialmente estável (memoizada pelo host):
   *  é dep do effect da busca, e uma arrow inline re-dispararia a cada render.
   *  Devolver o array DIRETO (sem Promise) é o catálogo local filtrado na mão
   *  (`SearchableSelectField`): a lista entra no mesmo render, sem passar pelo
   *  "buscando" — um catálogo que já está na memória não tem voo a mostrar. */
  onSearch: (term: string) => AsyncSelectOption[] | Promise<AsyncSelectOption[]>;
  onChange: (value: string, label: string) => void;
  /** Abaixo disto a lista mostra `promptText` em vez de buscar. `0` busca já
   *  com o campo vazio — é o que lista o catálogo inteiro ao abrir. */
  minChars?: number;
  debounceMs?: number;
  disabled?: boolean;
  id?: string;
}

/* Só as teclas que são DA LISTA. Home/End/PageUp ficam com o caret do input —
   roubá-las pro cmdk quebraria a edição do texto digitado. */
const LIST_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Enter']);

/** A veste do controle: a MESMA receita do `SelectTrigger` do DS (raio `lg`,
 *  borda `input`, anel de foco do kit), com a métrica vinda da casca
 *  compartilhada pra fileira não desalinhar. O foco mora no input DE DENTRO,
 *  então o anel é `focus-within` e não `focus-visible`. */
const CONTROL_CLASS =
  `flex ${FIELD_CONTROL_SIZE} min-w-0 items-center gap-1.5 rounded-lg border border-input ` +
  'bg-transparent px-2.5 text-sm transition-colors focus-within:border-ring focus-within:ring-3 ' +
  'focus-within:ring-ring/50';

/** O ciclo da busca remota: debounce + id monotônico de request, pra a resposta
 *  de uma query velha nunca sobrescrever a da mais nova. Promovido do
 *  `usePlaceSearch` do `PlaceField`. Erro degrada pro vazio (ver `emptyText`).
 *
 *  Só o DISPARO espera o debounce; a limpeza é imediata: o que passa pelo
 *  timer é o termo EFETIVO (vazio com o campo fechado ou abaixo de `minChars`),
 *  e o vazio se aplica na hora. Debounçar o termo cru deixava o valor atrasado
 *  sobreviver ao fechamento: reabrir dentro de `debounceMs` disparava a busca
 *  da vez anterior (e o "carregando") sobre um campo vazio.
 *
 *  Fechar NÃO apaga o resultado: o popover do Base UI segue montado na animação
 *  de saída, e opções zeradas ali faziam a lista moribunda dizer "nada
 *  encontrado" logo depois da escolha. Reaberto, o `pending` esconde a lista
 *  velha até o resultado novo assentar — mesmo com o mesmo termo. */
function useAsyncOptions(p: {
  open: boolean;
  term: string;
  minChars: number;
  debounceMs: number;
  onSearch: (term: string) => AsyncSelectOption[] | Promise<AsyncSelectOption[]>;
}) {
  const live = p.term.trim();
  /* `null`, e não `''`, é o "não busca": com `minChars: 0` o termo vazio é uma
     busca legítima (a que lista o catálogo inteiro), e o sentinela de string
     vazia a engoliria. `listed` é o termo que a lista RETRATA, aberta ou
     saindo; `effective` é o que se busca, só com o campo aberto. */
  const listed = live.length >= p.minChars ? live : null;
  const effective = p.open ? listed : null;
  const [query, setQuery] = useState(effective);
  // Closing (or dropping below `minChars`) stops the search in the same render.
  if (effective === null && query !== null) setQuery(null);
  useEffect(() => {
    if (effective === null) return undefined;
    const timer = setTimeout(() => {
      setQuery(effective);
    }, p.debounceMs);
    return () => {
      clearTimeout(timer);
    };
  }, [effective, p.debounceMs]);

  const canSearch = query !== null;
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<AsyncSelectOption[]>([]);
  /* O termo cujo resultado JÁ está em `options`. Entre o termo efetivo mudar e
     o resultado dele assentar há um ou dois renders com `options` vazio e
     `loading` falso — sem esta marca a lista dizia "nada encontrado" (e o
     `role="status"` anunciava) antes de ter procurado. */
  const [settled, setSettled] = useState<string | null>(null);
  /* Reabrir invalida o resultado retido: o catálogo pode ter mudado com o campo
     fechado, e o mesmo termo (o vazio do `minChars: 0`, ou o redigitado)
     mostraria opções velhas e escolhíveis até a nova busca assentar. Só a
     ABERTURA zera — na saída o resultado fica, pra lista moribunda não mudar. */
  const [wasOpen, setWasOpen] = useState(p.open);
  if (p.open !== wasOpen) {
    setWasOpen(p.open);
    if (p.open) setSettled(null);
  }
  const { onSearch } = p;
  useEffect(() => {
    if (query === null) return undefined;
    let alive = true;
    /* Inside the chain: a search that THROWS (sync) settles as empty like a
       rejection, and the local catalog's answer lands before the next paint. */
    Promise.resolve()
      .then(() => {
        const result = onSearch(query);
        // Catálogo local: a resposta já está na mão, entra sem o estado de voo.
        if (!Array.isArray(result) && alive) setLoading(true);
        return result;
      })
      .then((found) => {
        if (!alive) return;
        setOptions(found);
        setLoading(false);
        setSettled(query);
      })
      .catch(() => {
        if (!alive) return;
        setOptions([]);
        setLoading(false);
        setSettled(query);
      });
    return () => {
      alive = false;
    };
  }, [query, onSearch]);

  // Not searching means not loading: a flight cut short never reports back.
  const searching = canSearch && loading;
  const pending = listed !== null && !searching && settled !== listed;
  return { loading: searching, options, pending };
}

/** O que a lista diz quando não tem opção a mostrar: pedindo mais caracteres,
 *  em voo, ou vazia. `null` = há resultado a listar. O termo VIVO decide o
 *  pedido (a dica não espera o debounce). */
function listHint(
  p: {
    term: string;
    minChars: number;
    loading: boolean;
    empty: boolean;
  },
  texts: { promptText: string; loadingText: string; emptyText: string },
): string | null {
  if (p.term.trim().length < p.minChars) return texts.promptText;
  if (p.loading) return texts.loadingText;
  return p.empty ? texts.emptyText : null;
}

/** Clique no campo com o popover ABERTO é posicionamento de cursor no input,
 *  não pedido de fechar (mesma guarda do `PlaceField`). */
function closedByTriggerPress(details: { reason?: string } | undefined): boolean {
  return details?.reason === 'trigger-press';
}

/**
 * A casca do controle, pro `render` do `PopoverTrigger` (o Base UI espalha as
 * props dele por cima). `<div>` e não `<button>`: quem mora dentro é o
 * `<input>` da busca, e input dentro de button é HTML inválido.
 *
 * O gatilho é MUDO na árvore de acessibilidade — `role`, `aria-haspopup` e
 * `aria-expanded` do Base UI saem, e `tabIndex` fica em -1. Quem fala é o input:
 * o campo tem UM combobox, e a casca deixada como botão virava um segundo
 * controle focável, nomeado pelo texto que a pessoa acabou de digitar.
 * `pointer-events-none` + `aria-disabled` reencenam o `disabled` nativo; o
 * bloqueio de verdade está no `onOpenChange`.
 */
function ControlShell({
  disabled,
  tone,
  role: _role,
  'aria-haspopup': _hasPopup,
  'aria-expanded': _expanded,
  ...rest
}: { readonly disabled: boolean; readonly tone: string } & Record<string, unknown>) {
  return (
    <div
      {...rest}
      tabIndex={-1}
      aria-disabled={disabled || undefined}
      className={`${CONTROL_CLASS}${tone}${disabled ? ' pointer-events-none opacity-50' : ''}`}
    />
  );
}

/**
 * O input do campo. Aberto, encaminha ↑↓/Enter pro root do cmdk — o `Command`
 * mora no popover e escuta keydown no próprio root, com ou sem `CommandInput`;
 * é o que deixa o texto entrar pelo campo e a navegação ser da lista.
 *
 * **Foco NÃO abre a lista**, e fechado o input é `readOnly`: quem abre é o
 * clique (o gatilho do Base UI) ou ↓/Enter no teclado. Duas razões, as duas
 * observadas na tela: (1) escolher fecha o campo e o Base UI devolve o foco pro
 * gatilho — com foco-abre, a lista que a escolha fechou reabria sozinha, e o
 * vaivém deixava o popover presente no `data-ending-style`, visível e morto;
 * (2) fechado o input mostra o RÓTULO da escolha, então digitar por cima
 * semearia a busca com "CC-009 - Plataformaz".
 *
 * O Escape fecha SÓ este campo e MORRE aqui (`stopPropagation`): o checkout roda
 * em drawer, e sem o corte um Esc dirigido à lista derrubaria a camada de trás
 * junto.
 */
function SearchInput({
  id,
  label,
  describedBy,
  listId,
  open,
  term,
  shown,
  placeholder,
  disabled,
  required,
  panelRef,
  onTerm,
  onOpen,
  onEscape,
}: {
  readonly id: string;
  readonly label: string;
  /** A linha de `message` da casca, quando há. */
  readonly describedBy?: string;
  readonly listId: string;
  readonly open: boolean;
  readonly term: string;
  /** O que o campo mostra OCIOSO — rótulo da escolha, ou o valor cru. */
  readonly shown: string;
  readonly placeholder: string | undefined;
  readonly disabled: boolean;
  readonly required: boolean;
  readonly panelRef: RefObject<HTMLElement | null>;
  readonly onTerm: (term: string) => void;
  readonly onOpen: () => void;
  readonly onEscape: () => void;
}): ReactNode {
  const forward = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onEscape();
      return;
    }
    if (!LIST_KEYS.has(e.key)) return;
    // Fechado, ↓/Enter ABREM — é a única porta de teclado do campo (o padrão
    // WAI-ARIA do combobox), já que o foco não abre nada.
    if (!open) {
      e.preventDefault();
      onOpen();
      return;
    }
    const root = panelRef.current?.querySelector('[cmdk-root]');
    if (!root) return;
    e.preventDefault();
    root.dispatchEvent(new KeyboardEvent('keydown', { key: e.key, bubbles: true }));
  };
  return (
    <input
      id={id}
      type="text"
      role="combobox"
      aria-expanded={open}
      aria-controls={open ? listId : undefined}
      aria-autocomplete="list"
      aria-label={label}
      aria-describedby={describedBy}
      aria-required={required || undefined}
      disabled={disabled}
      readOnly={!open}
      placeholder={placeholder}
      value={open ? term : shown}
      onChange={(e) => {
        onTerm(e.target.value);
      }}
      onKeyDown={forward}
      /* A receita de vazio dos campos do DS: placeholder em muted e peso
         regular; o texto digitado volta pro corpo cheio. */
      className="w-full min-w-0 bg-transparent text-foreground outline-none placeholder:font-normal placeholder:text-muted-foreground"
    />
  );
}

export function AsyncSelectField({
  label,
  value,
  selectedLabel,
  placeholder,
  promptText,
  loadingText,
  emptyText,
  onSearch,
  onChange,
  minChars = 2,
  debounceMs = 300,
  disabled = false,
  id,
  adornment,
  adornmentLabel,
  message,
  messageTone,
  required = false,
}: Readonly<AsyncSelectFieldProps>) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const listId = `${generatedId}-list`;
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  /* Onde mora o `[cmdk-root]` do painel — é pra lá que o input encaminha as
     setas e o Enter. */
  const panelRef = useRef<HTMLDivElement>(null);

  const { loading, options, pending } = useAsyncOptions({
    open,
    term,
    minChars,
    debounceMs,
    onSearch,
  });

  const openField = () => {
    if (disabled || open) return;
    /* Abrir zera o rascunho: reabrir com a busca anterior mostraria a lista
       filtrada por algo que a pessoa não digitou agora. */
    setTerm('');
    setOpen(true);
  };
  /* Fechar mantém o termo: a lista em saída continua retratando a busca que a
     pessoa via (ver `useAsyncOptions`). Quem zera é a abertura. */
  const close = () => {
    setOpen(false);
  };
  const handleOpenChange = (next: boolean, details: { reason?: string } | undefined) => {
    if (next && disabled) return;
    if (!next && closedByTriggerPress(details)) return;
    /* Abrir zera o rascunho (como `openField`); fechar o mantém, pra lista em
       saída não trocar de conteúdo. */
    if (next) setTerm('');
    setOpen(next);
  };

  /* No vão em que o resultado do termo corrente ainda não assentou não há o
     que dizer: nem "nada encontrado" (não procurou) nem "buscando" (a busca
     local nem chega a existir). As opções desse vão são de OUTRO termo, então
     somem junto: a lista fica vazia. */
  const shownOptions = pending ? [] : options;
  const hint = pending
    ? null
    : listHint(
        { term, minChars, loading, empty: options.length === 0 },
        { promptText, loadingText, emptyText },
      );

  return (
    <FieldShell
      label={label}
      fieldId={fieldId}
      adornment={adornment}
      adornmentLabel={adornmentLabel}
      message={message}
      messageTone={messageTone}
    >
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger
          nativeButton={false}
          render={
            <ControlShell disabled={disabled} tone={fieldToneBorder({ message, messageTone })} />
          }
        >
          <SearchInput
            id={fieldId}
            label={adornment && adornmentLabel ? `${label} ${adornmentLabel}` : label}
            {...(message ? { describedBy: fieldMessageId(fieldId) } : {})}
            listId={listId}
            open={open}
            term={term}
            shown={selectedLabel || value}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            panelRef={panelRef}
            onTerm={setTerm}
            onOpen={openField}
            onEscape={close}
          />
          <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        </PopoverTrigger>
        {/* `w-(--anchor-width)`: a lista tem a largura do campo, que é a da
            coluna do formulário. `initialFocus={false}` mantém o foco no input
            DO CAMPO — deixar o popup puxá-lo desfaria a busca no instante em
            que ela abre. */}
        <PopoverContent
          align="start"
          className="w-(--anchor-width) p-1"
          initialFocus={false}
          aria-label={label}
        >
          <div ref={panelRef} className="contents">
            {/* `shouldFilter={false}`: quem filtra é o servidor. `bg-transparent
                p-0` porque a casca (fundo, raio, respiro) já é do
                `PopoverContent`. */}
            <Command shouldFilter={false} label={label} className="bg-transparent p-0">
              <CommandList id={listId}>
                {hint === null ? (
                  shownOptions.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      aria-current={option.value === value ? 'true' : undefined}
                      onSelect={() => {
                        onChange(option.value, option.label);
                        close();
                      }}
                    >
                      <span className="grid min-w-0 flex-1">
                        {/* `title`: o rótulo longo que a largura do campo corta
                            em reticências fica inteiro ao passar o mouse (#6792). */}
                        <span className="truncate text-foreground" title={option.label}>
                          {option.label}
                        </span>
                        {/* Espaço-texto entre as linhas: no grid ele não gera
                            caixa, mas entra no nome acessível — sem ele o leitor
                            de tela emenda rótulo e legenda numa palavra só. */}
                        {option.description ? ' ' : ''}
                        {option.description && (
                          <span className="truncate text-xs text-muted-foreground">
                            {option.description}
                          </span>
                        )}
                      </span>
                    </CommandItem>
                  ))
                ) : (
                  /* Frase de estado, não opção: `status` é o que faz o leitor de
                     tela anunciar "buscando"/"nada encontrado" sem a lista
                     fingir ter linha. A veste é a do `CommandEmpty` do kit. */
                  <div role="status" className="py-6 text-center text-sm">
                    {hint}
                  </div>
                )}
              </CommandList>
            </Command>
          </div>
        </PopoverContent>
      </Popover>
    </FieldShell>
  );
}
